/**
 * GraphQL Email Service
 * Handles SMTP config CRUD and email sending via AppSync
 */

import { generateClient } from 'aws-amplify/api';
import { EmailConfig, SendEmailInput, SendEmailResult } from '../types/emailConfig';

// `any`, as in leave.ts: the generated type sends TypeScript 5+ (the mobile app) into "excessive
// stack depth" (TS2321), and nothing here relies on it.
let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};
const graphqlExecutionCounts = new Map<string, number>();
const EMAIL_CONFIG_CACHE_TTL_MS = 60000;

type EmailConfigOptions = {
  force?: boolean;
};

let cachedEmailConfig: { fetchedAt: number; data: EmailConfig | null } | null = null;
let inFlightEmailConfig: Promise<EmailConfig | null> | null = null;

function getOperationName(query: unknown): string {
  const text = String(query || '');
  const match = text.match(/\b(query|mutation|subscription)\s+([A-Za-z0-9_]+)/);
  return match?.[2] || 'AnonymousGraphQL';
}

function graphqlWithLog(request: any): Promise<any> {
  const operationName = getOperationName(request?.query);
  const source = 'GraphQLEmailService';
  const countKey = `${source}:${operationName}`;
  const executionCount = (graphqlExecutionCounts.get(countKey) || 0) + 1;
  graphqlExecutionCounts.set(countKey, executionCount);
  console.log('GRAPHQL:', operationName, { source, executionCount });
  return getClient().graphql(request) as Promise<any>;
}

const GET_EMAIL_CONFIG = `
  query GetEmailConfig {
    getEmailConfig {
      configId
      smtpHost
      smtpPort
      smtpUsername
      smtpPassword
      encryption
      senderEmail
      senderName
      enabled
      timesheetCcEmail
      nominationReceiverEmail
      nominationCcEmail
      updatedAt
      updatedBy
    }
  }
`;

const GET_EMAIL_CONFIG_LEGACY = `
  query GetEmailConfig {
    getEmailConfig {
      configId
      smtpHost
      smtpPort
      smtpUsername
      smtpPassword
      encryption
      senderEmail
      senderName
      enabled
      timesheetCcEmail
      updatedAt
      updatedBy
    }
  }
`;

const SAVE_EMAIL_CONFIG = `
  mutation SaveEmailConfig($input: SaveEmailConfigInput!) {
    saveEmailConfig(input: $input) {
      configId
      smtpHost
      smtpPort
      smtpUsername
      smtpPassword
      encryption
      senderEmail
      senderName
      enabled
      timesheetCcEmail
      nominationReceiverEmail
      nominationCcEmail
      updatedAt
      updatedBy
    }
  }
`;

const SAVE_EMAIL_CONFIG_LEGACY = `
  mutation SaveEmailConfig($input: SaveEmailConfigInput!) {
    saveEmailConfig(input: $input) {
      configId
      smtpHost
      smtpPort
      smtpUsername
      smtpPassword
      encryption
      senderEmail
      senderName
      enabled
      timesheetCcEmail
      updatedAt
      updatedBy
    }
  }
`;

const SEND_EMAIL = `
  mutation SendEmail($input: SendEmailInput!) {
    sendEmail(input: $input) {
      success
      message
      messageId
    }
  }
`;

const TEST_EMAIL_CONFIG = `
  mutation TestEmailConfig {
    testEmailConfig {
      success
      message
      messageId
    }
  }
`;

export class GraphQLEmailService {
  /**
   * Get current SMTP configuration
   */
  static async getEmailConfig(options: EmailConfigOptions = {}): Promise<EmailConfig | null> {
    const now = Date.now();
    if (!options.force && cachedEmailConfig && now - cachedEmailConfig.fetchedAt < EMAIL_CONFIG_CACHE_TTL_MS) {
      console.log('[GraphQLEmailService] Using cached email config');
      return cachedEmailConfig.data;
    }

    if (!options.force && inFlightEmailConfig) {
      console.log('[GraphQLEmailService] Reusing in-flight email config request');
      return inFlightEmailConfig;
    }

    inFlightEmailConfig = this.fetchEmailConfig();
    try {
      const config = await inFlightEmailConfig;
      cachedEmailConfig = {
        fetchedAt: Date.now(),
        data: config,
      };
      return config;
    } finally {
      inFlightEmailConfig = null;
    }
  }

  private static async fetchEmailConfig(): Promise<EmailConfig | null> {
    try {
      const startedAt = performance.now();
      console.log('[Perf][NominationRole][Start] GraphQLEmailService.getEmailConfig extended query start');
      const response = await graphqlWithLog({
        query: GET_EMAIL_CONFIG,
      }) as any;
      console.log('[Perf][NominationRole][SettingsLoaded] GraphQLEmailService.getEmailConfig extended query complete', {
        durationMs: Math.round(performance.now() - startedAt),
        hasConfig: Boolean(response.data.getEmailConfig),
      });
      return response.data.getEmailConfig || null;
    } catch (error) {
      console.warn('[GraphQLEmailService] Failed to get extended email config, retrying legacy query:', error);
      try {
        const legacyStartedAt = performance.now();
        console.log('[Perf][NominationRole][Start] GraphQLEmailService.getEmailConfig legacy query start');
        const response = await graphqlWithLog({
          query: GET_EMAIL_CONFIG_LEGACY,
        }) as any;
        console.log('[Perf][NominationRole][SettingsLoaded] GraphQLEmailService.getEmailConfig legacy query complete', {
          durationMs: Math.round(performance.now() - legacyStartedAt),
          hasConfig: Boolean(response.data.getEmailConfig),
        });
        return response.data.getEmailConfig || null;
      } catch (legacyError) {
        console.error('[GraphQLEmailService] Failed to get email config:', legacyError);
        console.log('[Perf][NominationRole][SettingsLoaded] GraphQLEmailService.getEmailConfig returning null after legacy failure');
        return null;
      }
    }
  }

  /**
   * Save SMTP configuration (password is base64-encoded before saving)
   */
  static async saveEmailConfig(config: Omit<EmailConfig, 'configId' | 'updatedAt' | 'updatedBy'>): Promise<EmailConfig | null> {
    /*
     * Fields the caller does not own are carried over from the stored record.
     *
     * The resolver behind this is a PutItem — it replaces the whole row and writes
     * `$util.defaultIfNull(input.nominationReceiverEmail, "")`. So any screen that saves
     * without sending the nomination fields silently blanks them, and there are two
     * screens writing this one record: Email Settings (SMTP) and Nomination Settings.
     * Email Settings does not edit the nomination receiver, so it must not be able to
     * erase it — which is how the configured receiver ends up empty and the Review Queue
     * and Manage Nominations quietly stop appearing for that person.
     *
     * `??`, not `||`: an empty string is a deliberate "clear this", and only `undefined`
     * means "I am not the owner of this field".
     */
    const needsPreserved =
      config.nominationReceiverEmail === undefined || config.nominationCcEmail === undefined;
    const stored = needsPreserved
      ? await this.getEmailConfig({ force: true }).catch(() => null)
      : null;

    const encodedConfig = {
      ...config,
      nominationReceiverEmail: config.nominationReceiverEmail ?? stored?.nominationReceiverEmail ?? '',
      nominationCcEmail: config.nominationCcEmail ?? stored?.nominationCcEmail ?? '',
      smtpPassword: btoa(config.smtpPassword),
    };

    try {
      const response = await graphqlWithLog({
        query: SAVE_EMAIL_CONFIG,
        variables: { input: encodedConfig },
      }) as any;
      const savedConfig = response.data.saveEmailConfig || null;
      cachedEmailConfig = { fetchedAt: Date.now(), data: savedConfig };
      return savedConfig;
    } catch (error) {
      console.warn('[GraphQLEmailService] Failed to save extended email config, retrying legacy mutation:', error);
      try {
        /*
         * The legacy input has no nomination fields, and the resolver defaults anything
         * absent to "" on a whole-row PutItem — so this retry ERASES the configured
         * nomination receiver and cc. It is kept for a deployment whose schema predates
         * those fields, where the alternative is the save failing outright, but it is a
         * silent data loss and has to be visible when it happens.
         */
        const { nominationReceiverEmail, nominationCcEmail, ...legacyConfig } = encodedConfig;
        if (nominationReceiverEmail || nominationCcEmail) {
          console.error(
            '[GraphQLEmailService] Legacy saveEmailConfig cannot carry nomination settings — ' +
            'the configured receiver/cc will be cleared by this save. Re-enter them in ' +
            'Nomination Settings once the API schema is up to date.',
            { nominationReceiverEmail, nominationCcEmail }
          );
        }
        const response = await graphqlWithLog({
          query: SAVE_EMAIL_CONFIG_LEGACY,
          variables: { input: legacyConfig },
        }) as any;
        const savedConfig = response.data.saveEmailConfig || null;
        cachedEmailConfig = { fetchedAt: Date.now(), data: savedConfig };
        return savedConfig;
      } catch (legacyError) {
        console.error('[GraphQLEmailService] Failed to save email config:', legacyError);
        cachedEmailConfig = null;
        throw legacyError;
      }
    }
  }

  /**
   * Send an email via the Lambda email sender
   */
  static async sendEmail(input: SendEmailInput): Promise<SendEmailResult> {
    try {
      const response = await graphqlWithLog({
        query: SEND_EMAIL,
        variables: { input },
      }) as any;
      return response.data.sendEmail;
    } catch (error) {
      console.warn('[GraphQLEmailService] sendEmail failed with default auth, retrying with apiKey:', error);
      try {
        const response = await graphqlWithLog({
          query: SEND_EMAIL,
          variables: { input },
          authMode: 'apiKey',
        }) as any;
        return response.data.sendEmail;
      } catch (apiKeyError) {
        console.error('[GraphQLEmailService] Failed to send email:', apiKeyError);
        return { success: false, message: `Failed: ${apiKeyError}` };
      }
    }
  }

  /**
   * Test SMTP configuration by sending a test email
   */
  static async testEmailConfig(): Promise<SendEmailResult> {
    try {
      const response = await graphqlWithLog({
        query: TEST_EMAIL_CONFIG,
      }) as any;
      return response.data.testEmailConfig;
    } catch (error) {
      console.warn('[GraphQLEmailService] testEmailConfig failed with default auth, retrying with apiKey:', error);
      try {
        const response = await graphqlWithLog({
          query: TEST_EMAIL_CONFIG,
          authMode: 'apiKey',
        }) as any;
        return response.data.testEmailConfig;
      } catch (apiKeyError: any) {
        console.error('[GraphQLEmailService] Failed to test email config:', apiKeyError);
        /*
         * Amplify rejects with a `{ data, errors }` object, not an Error. Interpolating it
         * straight into the string produced "Test failed: [object Object]" — the operator was
         * told the test failed and given nothing whatsoever to act on. The real reason is in
         * errors[0].message.
         */
        const detail =
          apiKeyError?.errors?.[0]?.message ||
          apiKeyError?.message ||
          'the request did not reach the email service.';
        return { success: false, message: `Test failed: ${detail}` };
      }
    }
  }
}
